import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useToast } from '@/shared/hooks/useToast';
import i18n from '@/core/i18n/config';
import { AI_TASK_PLAN_PROPOSE_ERROR_MAP, AI_TASK_PLAN_APPLY_ERROR_MAP } from '../errors';
import { resolveAiError } from '../errors.resolve';
import { useTaskPlanProposal } from './useTaskPlanProposal';
import { useApplyTaskPlan } from './useApplyTaskPlan';
import { useVoiceDictation, VOICE_ERROR_KEY } from './useVoiceDictation';
import { computeTaskPlanSelection } from '../utils/taskPlanSelection';
import { formatMinutes } from '@/features/tasks/public';
import type { TaskPlanDraftFormValues } from '../schemas';
import type { TaskPlanProposal } from '../types';

export const RETRYABLE_ERROR_KEY = 'ai:errors.retryable';

/**
 * All state and handlers behind `AiGoalPanel`: the goal, the proposal, the
 * per-row edits and selection, and the propose/apply mutations. Component
 * state, not Zustand, because nothing outside the panel reads it and it dies
 * with the panel.
 */
export function useAiGoalPanel(projectId: string, boardId: string, onClose: () => void) {
  const { t } = useTranslation('ai');
  const { toast } = useToast();
  const proposalMutation = useTaskPlanProposal(projectId, boardId);
  const applyMutation = useApplyTaskPlan(projectId, boardId);

  const [goal, setGoal] = useState('');
  const [proposal, setProposal] = useState<TaskPlanProposal | null>(null);
  const [drafts, setDrafts] = useState<TaskPlanDraftFormValues[]>([]);
  const [proposeErrorKey, setProposeErrorKey] = useState<string | null>(null);
  const [applyErrorKey, setApplyErrorKey] = useState<string | null>(null);

  // Same dictation the quick-create panel offers: a goal is the longer of the two things
  // the user types here, so if either field is worth speaking it is this one.
  const voiceDictation = useVoiceDictation({
    onTranscription: (text) => setGoal(text),
    onError: (error) => {
      toast.error({ title: t(VOICE_ERROR_KEY[error]) });
    },
    language: i18n.language,
  });

  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const errorKey = proposeErrorKey ?? applyErrorKey;
  const isProposePending = proposalMutation.isPending;
  const canPropose = goal.trim().length > 0 && !isProposePending;

  const selection = computeTaskPlanSelection(drafts);
  const hasTitleError = drafts.some((d) => d.selected && d.title.trim().length === 0);

  /** Whether the user made any edits or deselections worth warning about. */
  function isDirty() {
    if (!proposal) return false;
    return drafts.some((d, i) => {
      const original = proposal.tasks[i];
      if (!original) return false;
      return (
        !d.selected ||
        d.title !== original.title ||
        d.priority !== original.priority ||
        d.estimatedMinutes !== original.estimatedMinutes
      );
    });
  }

  function clearErrors() {
    setProposeErrorKey(null);
    setApplyErrorKey(null);
  }

  function runPropose(goalText: string) {
    clearErrors();
    proposalMutation.mutate(
      { goal: goalText, timeZoneId },
      {
        onSuccess: (result) => {
          setProposal(result);
          setDrafts(
            result.tasks.map((task) => ({
              title: task.title,
              priority: task.priority,
              estimatedMinutes: task.estimatedMinutes,
              selected: true,
            })),
          );
          toast.success({ title: t('quickCreate.toasts.proposalGenerated') });
        },
        onError: (error) => {
          setProposeErrorKey(resolveAiError(AI_TASK_PLAN_PROPOSE_ERROR_MAP, 'POST', error));
        },
      },
    );
  }

  /**
   * Generating again replaces the proposal on screen, and with it any field
   * the user already corrected — so it asks first once there is something to lose.
   * Dirty-aware: confirms only when edits or deselections exist.
   */
  async function handleGenerate() {
    if (proposal && isDirty()) {
      const { confirmed } = await confirm({
        title: t('goalPanel.confirmRegenerate.title'),
        description: t('goalPanel.confirmRegenerate.description'),
        confirmLabel: t('goalPanel.confirmRegenerate.confirm'),
        cancelLabel: t('goalPanel.confirmRegenerate.cancel'),
      });
      if (!confirmed) return;
    }
    runPropose(goal);
  }

  function handleDraftChange(index: number, patch: Partial<TaskPlanDraftFormValues>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  }

  function handleApply() {
    if (!proposal || selection.selectedCount === 0 || hasTitleError) return;
    clearErrors();
    applyMutation.mutate(
      { drafts, proposal },
      {
        onSuccess: (result) => {
          const count = result.createdCount ?? selection.selectedCount;
          toast.success({
            title: t('goalPanel.toasts.tasksCreated', { count }),
          });
          onClose();
        },
        onError: (error) => {
          setApplyErrorKey(resolveAiError(AI_TASK_PLAN_APPLY_ERROR_MAP, 'POST', error));
        },
      },
    );
  }

  function estimatedLabel() {
    if (selection.totalEstimatedMinutes == null) return null;
    const time = formatMinutes(selection.totalEstimatedMinutes);
    if (selection.hasUnestimated) {
      return t('goalPanel.estimatedPartial', { time, unestimated: selection.unestimatedCount });
    }
    return t('goalPanel.estimatedTotal', { time });
  }

  return {
    goal,
    setGoal,
    proposal,
    drafts,
    errorKey,
    proposeErrorKey,
    isProposePending,
    canPropose,
    voiceDictation,
    selection,
    hasTitleError,
    estimated: estimatedLabel(),
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleGenerate,
    handleDraftChange,
    handleApply,
    runPropose,
    isApplyPending: applyMutation.isPending,
  };
}
